/**
 * Passport Reactions ("Sparks") API client.
 *
 * Explorer→explorer low-friction reactions on passports. Reacting is free and
 * the resulting chat (started by the passport owner) is free too — neither path
 * consumes the monthly reach-out quota.
 */

type ApiCall = (url: string, options?: RequestInit) => Promise<Response>;

const apiBase = () => process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/**
 * Master switch for the Passport Reactions ("Sparks") UI.
 *
 * Backend (messaging-service + gateway) stays deployed and functional; this
 * only controls whether the feature is *visible* in the app. Flip to `true`
 * to launch. While `false`: no reaction bars, no Sparks inbox section, no
 * reaction badge.
 */
export const ENABLE_PASSPORT_REACTIONS = true;

export type ReactionKey = 'slay' | 'goated' | 'cooking';

export interface ReactionDef {
  key: ReactionKey;
  emoji: string;
  label: string;
}

/** Curated, ordered reactions. Keep keys in sync with messaging-service ALLOWED_REACTIONS. */
export const REACTIONS: ReactionDef[] = [
  { key: 'slay', emoji: '💅', label: 'Slay' },
  { key: 'goated', emoji: '🐐', label: 'Goated' },
  { key: 'cooking', emoji: '🍳', label: 'Cooking' },
];

export const REACTION_BY_KEY: Record<string, ReactionDef> = REACTIONS.reduce(
  (acc, r) => { acc[r.key] = r; return acc; },
  {} as Record<string, ReactionDef>,
);

export interface ReceivedReaction {
  id: number;
  reaction: ReactionKey;
  created_at: number;
  is_seen: boolean;
  conversation_id: string | null;
  /** Reactor's current CV id (resolved live; null if they removed their CV). */
  reactor_cv_id: string | null;
}

export type ReactionAction = 'add' | 'remove';

export async function togglePassportReaction(
  apiCall: ApiCall,
  cvId: string,
  reaction: ReactionKey,
  action: ReactionAction = 'add',
): Promise<{ action: 'added' | 'already' | 'removed' | 'noop'; reaction: ReactionKey }> {
  const res = await apiCall(`${apiBase()}/api/v1/messaging/passport-reactions/by-cv`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cv_id: cvId, reaction, action }),
  });
  if (!res.ok) throw new Error(`Failed to ${action} reaction (${res.status})`);
  return res.json();
}

/** The current user's own reaction keys on a passport (to seed toggle state). */
export async function getMyReactionsByCv(apiCall: ApiCall, cvId: string): Promise<ReactionKey[]> {
  const res = await apiCall(`${apiBase()}/api/v1/messaging/passport-reactions/mine-by-cv?cv_id=${encodeURIComponent(cvId)}`);
  if (!res.ok) return [];
  const data = await res.json();
  return (data.reactions || []) as ReactionKey[];
}

/**
 * All reactions the current user has sent, grouped by the targeted CV id.
 * One call to seed a whole feed of reaction bars (avoids N per-card requests).
 * Keyed on the cv_id snapshot at reaction time — a card whose owner re-uploaded
 * simply won't pre-highlight, which is harmless (re-tapping returns 'already').
 */
export async function getMyReactionsAllByCv(
  apiCall: ApiCall,
): Promise<Record<string, ReactionKey[]>> {
  const res = await apiCall(`${apiBase()}/api/v1/messaging/passport-reactions/mine-all`);
  if (!res.ok) return {};
  const data = await res.json();
  const map: Record<string, ReactionKey[]> = {};
  for (const r of (data.reactions || []) as Array<{ target_cv_id: string | null; reaction: ReactionKey }>) {
    if (!r.target_cv_id) continue;
    (map[r.target_cv_id] ||= []).push(r.reaction);
  }
  return map;
}

export async function getReceivedReactions(apiCall: ApiCall): Promise<ReceivedReaction[]> {
  const res = await apiCall(`${apiBase()}/api/v1/messaging/passport-reactions/received`);
  if (!res.ok) throw new Error(`Failed to load reactions (${res.status})`);
  const data = await res.json();
  return (data.reactions || []) as ReceivedReaction[];
}

export async function getReactionsUnreadCount(apiCall: ApiCall): Promise<number> {
  const res = await apiCall(`${apiBase()}/api/v1/messaging/passport-reactions/unread-count`);
  if (!res.ok) return 0;
  const data = await res.json();
  return data.unread_count || 0;
}

export async function markReactionsSeen(apiCall: ApiCall): Promise<void> {
  await apiCall(`${apiBase()}/api/v1/messaging/passport-reactions/mark-seen`, { method: 'POST' });
}

export async function startChatFromReaction(
  apiCall: ApiCall,
  reactionId: number,
): Promise<{ conversation_id: string; already_existed: boolean }> {
  const res = await apiCall(
    `${apiBase()}/api/v1/messaging/passport-reactions/${reactionId}/start-chat`,
    { method: 'POST' },
  );
  if (!res.ok) throw new Error(`Failed to start chat (${res.status})`);
  return res.json();
}
